/**
 * Workflow Order Service
 *
 * Unified service for order workflow operations.
 *
 * Use this service in components for workflow operations.
 *
 * @see documentation/backend-api-spec.md for API documentation
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, finalize, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OrderStatusId, getStatusConfig } from '../config/order-statuses.config';
import { getPhaseForStatus, PIPELINE_STAGES } from '../config/order-workflow.config';
import {
  ApiOrder,
  StatusTransition,
  TransitionStatusRequest,
  TransitionStatusResponse,
  AvailableActionsResponse,
  ExecuteActionResponse,
  OrderFilters,
  PaginationParams,
  PaginatedResponse,
  CreateOrderRequest,
} from '../models/order-state.model';
import { formatMoney } from '../utils/money-format';
import { orderCustomerTotal } from '../utils/order-total';
import { writtenUnit } from '../utils/quantity-unit';
import { TranslationService } from './i18n/translation.service';

@Injectable({
  providedIn: 'root',
})
export class WorkflowOrderService {
  private http = inject(HttpClient);
  private i18n = inject(TranslationService);
  private apiUrl = `${environment.apiBaseUrl}/orders/workflow/orders`;
  private orderApiUrl = `${environment.apiBaseUrl}/orders/orders`; // Regular order API endpoint

  // ==========================================================================
  // CACHE (60s TTL on list, 30s SWR on per-order endpoints)
  // ==========================================================================
  private static readonly LIST_TTL_MS = 60_000;
  private static readonly DETAIL_TTL_MS = 30_000;

  private listCache = new Map<
    string,
    { obs$: Observable<PaginatedResponse<ApiOrder>>; fetchedAt: number }
  >();
  private byIdCache = new Map<string, { data: ApiOrder; fetchedAt: number }>();
  private detailsCache = new Map<string, { data: any; fetchedAt: number }>();
  private workflowCache = new Map<string, { data: any; fetchedAt: number }>();
  /** The order detail reads on their way: a second caller joins the first, no second GET */
  private detailsInFlight = new Map<string, Observable<any>>();
  /** The same for the workflow reads (the timeline and the quoting stage read it together) */
  private workflowInFlight = new Map<string, Observable<any>>();
  /** Bumped by invalidateOrder (one order) and invalidateAll (every order): a read started
   *  before a write never fills the cache after it */
  private generations = new Map<string, number>();
  private allGeneration = 0;

  /** Wipe all cached read state: after an orders write that names no one order. */
  invalidateAll(): void {
    this.listCache.clear();
    this.byIdCache.clear();
    this.detailsCache.clear();
    this.workflowCache.clear();
    this.detailsInFlight.clear();
    this.workflowInFlight.clear();
    this.allGeneration++;
  }

  /** Drop cache entries for one order plus the list (which may contain it). */
  invalidateOrder(id: string | number | null | undefined): void {
    const key = id?.toString();
    if (!key) return;
    this.byIdCache.delete(key);
    this.detailsCache.delete(key);
    this.workflowCache.delete(key);
    this.detailsInFlight.delete(key);
    this.workflowInFlight.delete(key);
    this.generations.set(key, (this.generations.get(key) ?? 0) + 1);
    this.listCache.clear();
  }

  /** Store a read for this order only if no write invalidated it since the read started. */
  private cacheIfCurrent<T>(cache: Map<string, { data: T; fetchedAt: number }>, key: string) {
    const generation = this.generations.get(key);
    const allGeneration = this.allGeneration;
    return tap((data: T) => {
      if (this.generations.get(key) === generation && this.allGeneration === allGeneration) {
        cache.set(key, { data, fetchedAt: Date.now() });
      }
    });
  }

  private buildListCacheKey(filters?: OrderFilters, pagination?: PaginationParams): string {
    return JSON.stringify({ filters: filters ?? {}, pagination: pagination ?? {} });
  }

  // ==========================================================================
  // CRUD OPERATIONS
  // ==========================================================================

  /**
   * Get orders with optional filtering and pagination.
   * Cached per (filters, pagination) tuple for LIST_TTL_MS.
   */
  getOrders(
    filters?: OrderFilters,
    pagination?: PaginationParams
  ): Observable<PaginatedResponse<ApiOrder>> {
    const cacheKey = this.buildListCacheKey(filters, pagination);
    const cached = this.listCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < WorkflowOrderService.LIST_TTL_MS) {
      return cached.obs$;
    }

    let params = new HttpParams();

    if (pagination) {
      if (pagination.page) params = params.set('page', pagination.page.toString());
      if (pagination.pageSize) params = params.set('page_size', pagination.pageSize.toString());
      if (pagination.sortBy) {
        const ordering =
          pagination.sortOrder === 'desc' ? `-${pagination.sortBy}` : pagination.sortBy;
        params = params.set('ordering', ordering);
      }
    }

    if (filters) {
      if (filters.customer) params = params.set('customer', filters.customer.toString());
      if (filters.isBlocked !== undefined)
        params = params.set('is_blocked', filters.isBlocked.toString());
      if (filters.month) params = params.set('month', filters.month);
      if (filters.orderDate) params = params.set('order_date', filters.orderDate);
      if (filters.timeline_stage !== undefined)
        params = params.set('timeline_stage', filters.timeline_stage.toString());
      if (filters.search) params = params.set('search', filters.search);
      if (filters.status) params = params.set('status', filters.status);
      if (filters.startDate) params = params.set('start_date', filters.startDate);
      if (filters.endDate) params = params.set('end_date', filters.endDate);
      // The API reads ?user= (created_by); ?user_id= was ignored, so the filter did nothing
      if (filters.userId) params = params.set('user', filters.userId);
      if (filters.supplierId) params = params.set('supplier', filters.supplierId);
      if (filters.statusCategory)
        params = params.set('status_category', filters.statusCategory.toLocaleLowerCase());
      if (filters.paymentOverdue) params = params.set('payment_overdue', 'true');
      if (filters.closed !== undefined) params = params.set('closed', String(filters.closed));
      if (filters.delivered) params = params.set('delivered', 'true');
      if (filters.country) params = params.set('country', filters.country);
    }

    console.log(filters);
    const obs$ = this.http.get<any>(`${this.apiUrl}/`, { params }).pipe(
      map((response) => ({
        items: response.results?.map((item: any) => this.mapBackendToFrontend(item)) || [],
        total: response.count || 0,
        page: pagination?.page || 1,
        pageSize: pagination?.pageSize || response.results?.length || 50,
        totalPages: Math.ceil((response.count || 0) / (pagination?.pageSize || 50)),
      })),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.listCache.set(cacheKey, { obs$, fetchedAt: Date.now() });
    return obs$;
  }

  /**
   * Get all orders as flat array (convenience method)
   */
  getAllOrders(): Observable<ApiOrder[]> {
    return this.getOrders().pipe(map((response) => response.items));
  }

  /**
   * Resolve a display order number (e.g. ORD-2025-2459) to the numeric PK
   * the workflow detail routes expect. Numeric ids pass through unchanged.
   */
  resolveNumericOrderId(orderId: string): Observable<string> {
    if (/^\d+$/.test(orderId)) {
      return of(orderId);
    }
    return this.http
      .get<any>(`${this.orderApiUrl}/`, { params: { search: orderId, page_size: '5' } })
      .pipe(
        map((res) => {
          const results = Array.isArray(res?.results) ? res.results : Array.isArray(res) ? res : [];
          const match = results.find(
            (o: any) =>
              o.order_number === orderId || o.order_id === orderId || String(o.id) === orderId
          );
          if (match?.id == null) {
            // The same 404 the detail call gives, so callers treat both as "no such order"
            throw new HttpErrorResponse({ status: 404, statusText: `Order ${orderId} not found` });
          }
          return String(match.id);
        })
      );
  }

  /**
   * One order as a timeline list row (customer, lines, total, stage), for an
   * order that is not on the loaded page. Stale-while-revalidate: returns the
   * cache instantly if present; refreshes in background when stale.
   */
  getOrderById(id: string): Observable<ApiOrder> {
    id = id.toString(); // one key per order, whatever type the caller passed
    const cached = this.byIdCache.get(id);
    const stale = !cached || Date.now() - cached.fetchedAt > WorkflowOrderService.DETAIL_TTL_MS;

    // The workflow detail route answers a workflow summary with no customer,
    // lines or total, so mapping it gave "Desconocido / Producto sin nombre /
    // Por definir". Read the same list row the timeline shows for every other
    // order (found by its number); the order detail is the fallback. The detail is a fresh
    // or in-flight getOrderDetails read, so opening the order does not read it again.
    if (cached && !stale) return of(cached.data);
    const details = this.detailsCache.get(id);
    const details$ =
      details && Date.now() - details.fetchedAt <= WorkflowOrderService.DETAIL_TTL_MS
        ? of(details.data)
        : this.readOrder(id);
    const fresh$ = details$.pipe(
      switchMap((order) =>
        this.http
          .get<any>(`${this.apiUrl}/`, {
            params: { search: order.order_number || id, page_size: '5' },
          })
          .pipe(
            map(
              (res) =>
                (res?.results || []).find((row: any) => String(row.id) === String(id)) ?? order
            )
          )
      ),
      map((item) => this.mapBackendToFrontend(item)),
      this.cacheIfCurrent(this.byIdCache, id),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    if (cached) {
      fresh$.subscribe({
        error: () => {
          /* keep using cached */
        },
      });
      return of(cached.data);
    }
    return fresh$;
  }

  /**
   * Get full order details from regular order endpoint.
   * Stale-while-revalidate; reads go through readOrder, so a second caller joins one on its way.
   */
  getOrderDetails(id: string): Observable<any> {
    id = id.toString(); // one key per order, whatever type the caller passed
    const cached = this.detailsCache.get(id);
    const stale = !cached || Date.now() - cached.fetchedAt > WorkflowOrderService.DETAIL_TTL_MS;

    if (cached && !stale) return of(cached.data);
    const fresh$ = this.readOrder(id);
    if (cached && stale) {
      fresh$.subscribe({
        error: () => {
          /* keep using cached */
        },
      });
      return of(cached.data);
    }
    return fresh$;
  }

  /** The order read from the server (opening the order, a prefetch): one read per order on its way. */
  private readOrder(id: string): Observable<any> {
    return this.joinedRead(
      this.detailsInFlight,
      this.detailsCache,
      id,
      `${this.orderApiUrl}/${id}/`
    );
  }

  /** A read from the server that fills the cache; one already on its way is joined, not sent again. */
  private joinedRead(
    inFlight: Map<string, Observable<any>>,
    cache: Map<string, { data: any; fetchedAt: number }>,
    key: string,
    url: string
  ): Observable<any> {
    const pending = inFlight.get(key);
    if (pending) return pending;
    const read$ = this.http.get<any>(url).pipe(
      this.cacheIfCurrent(cache, key),
      finalize(() => {
        if (inFlight.get(key) === read$) inFlight.delete(key);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    inFlight.set(key, read$);
    return read$;
  }

  /**
   * Create a new order
   */
  createOrder(request: CreateOrderRequest): Observable<ApiOrder> {
    const body = {
      customer: null,
      unit_price: '0',
      quantity_ordered: request.quantity,
      currency: 'USD',
      status_id: 'REVIEW',
    };

    return this.http.post<any>(`${this.apiUrl}/`, body).pipe(
      map((item) => this.mapBackendToFrontend(item)),
      tap(() => this.invalidateAll())
    );
  }

  /**
   * Delete an order
   */
  deleteOrder(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/`).pipe(tap(() => this.invalidateAll()));
  }

  // ==========================================================================
  // WORKFLOW TRANSITIONS
  // ==========================================================================

  /**
   * Transition order to a new status
   */
  transitionStatus(
    orderId: string,
    request: TransitionStatusRequest
  ): Observable<TransitionStatusResponse> {
    const body = {
      to_status_id: request.toStatusId,
      notes: request.notes,
      metadata: request.metadata,
    };

    return this.http.patch<any>(`${this.apiUrl}/${orderId}/status/`, body).pipe(
      map((workflowSummary) => ({
        success: true,
        order: this.buildOrderFromWorkflow(orderId, workflowSummary),
        transition: {
          id: `trans-${Date.now()}`,
          from: workflowSummary.previousStatus?.id || null,
          to: workflowSummary.currentStatus.id,
          timestamp: new Date().toISOString(),
          triggeredBy: 'user' as const,
          notes: request.notes,
          metadata: request.metadata,
        },
      }))
    );
  }

  /**
   * Get status transition history for an order
   */
  getOrderHistory(orderId: string): Observable<StatusTransition[]> {
    return this.http
      .get<any>(`${this.apiUrl}/${orderId}/history/`)
      .pipe(map((response) => (response.results || []).map(this.mapTransition)));
  }

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  /**
   * Get available actions for an order's current status
   */
  getAvailableActions(orderId: string): Observable<AvailableActionsResponse> {
    return this.http.get<any>(`${this.apiUrl}/${orderId}/actions/`).pipe(
      map((response) => ({
        orderId,
        currentStatusId: 'UNKNOWN' as any,
        actions: (response.results || []).map((a: any) => ({
          id: a.id,
          label: a.label,
          description: a.description || '',
          icon: a.icon || '📋',
          targetStatusId: a.targetStatusId,
          requiresConfirmation: a.requiresInput || false,
          isDestructive: a.isDestructive || false,
        })),
      }))
    );
  }

  /**
   * Execute an action on an order
   */
  executeAction(
    orderId: string,
    actionId: string,
    data?: Record<string, unknown>
  ): Observable<ExecuteActionResponse> {
    return this.http.post<any>(`${this.apiUrl}/${orderId}/actions/${actionId}/`, data || {}).pipe(
      map((workflowSummary) => ({
        success: true,
        order: this.buildOrderFromWorkflow(orderId, workflowSummary),
        message: workflowSummary.message,
      }))
    );
  }

  /**
   * Get communications/emails for an order
   * Endpoint: /api/orders/orders/{id}/communications/
   */
  getOrderCommunications(orderId: string): Observable<any> {
    const url = `${this.orderApiUrl}/${orderId}/communications/`;
    console.log('Fetching order communications from:', url);
    return this.http.get<any>(url);
  }

  /**
   * Get communications/emails for an order with pagination
   * Endpoint: /api/orders/orders/{id}/communications/?page={page}&page_size={page_size}
   */
  getOrderCommunicationsWithPagination(
    orderId: string,
    params: { page: string; page_size: string; mentions?: 'true' }
  ): Observable<any> {
    const url = `${this.orderApiUrl}/${orderId}/communications/`;
    console.log('📨 [WorkflowOrderService] Fetching communications with pagination:', url, params);
    return this.http.get<any>(url, { params });
  }

  // ==========================================================================
  // VALIDATION (Client-side helpers)
  // ==========================================================================

  /**
   * Check if a transition is valid (client-side)
   */
  /*  canTransition(fromStatus: OrderStatusId, toStatus: OrderStatusId): boolean {
    return this.mockApi.canTransition(fromStatus, toStatus);
  } */

  /**
   * Get valid next statuses (client-side)
   */
  /* getValidTransitions(statusId: OrderStatusId): OrderStatusId[] {
    return this.mockApi.getValidTransitions(statusId);
  } */

  // ==========================================================================
  // UTILITY
  // ==========================================================================

  /**
   * Reset data to initial seed state (mock only)
   */
  resetData(): Observable<void> {
    throw new Error('resetData is only available in mock mode');
  }

  /**
   * Check if running in mock mode
   */
  readonly isMockMode = false;

  // ==========================================================================
  // BACKEND DATA MAPPING HELPERS
  // ==========================================================================

  /**
   * Map backend order data to frontend ApiOrder format
   */
  private mapBackendToFrontend(backend: any): ApiOrder {
    const statusId = backend.current_status_id || backend.status_id || 'REVIEW';
    const statusConfig = getStatusConfig(statusId as OrderStatusId);
    const phase = getPhaseForStatus(statusId as OrderStatusId);
    const orderValue = orderCustomerTotal(backend);

    return {
      id: backend.order_number || backend.id?.toString() || 'unknown',
      backendId: backend.id?.toString() || undefined, // Store numeric ID for API calls
      // The server's flag: an order made from an email has status "new" too, and only a draft can be rejected (card
      // 1089); status "new" stays the answer of a server without it
      isDraft: backend.is_draft ?? backend.status === 'new',
      isOpportunity: backend.is_opportunity || false,
      client: backend.customer_name || this.i18n.t('common.unknown'),
      clientEmail: backend.customer_email,
      companyEmailName: backend.company_email_name,
      companyEmail: backend.company_email,
      product: (() => {
        const items = backend.items || [];
        if (items.length > 0) {
          const firstName =
            items[0].product__name ||
            items[0].product_name ||
            items[0].name ||
            this.i18n.t('common.unknownProduct');
          return items.length > 1
            ? this.i18n.t('common.productAndMore', { name: firstName })
            : firstName;
        }
        return backend.product_name || this.i18n.t('common.unknownProduct');
      })(),
      quantity: backend.quantity_ordered
        ? `${backend.quantity_ordered} ${writtenUnit(backend.quantity_unit)}`.trim()
        : this.i18n.t('common.tbd'),
      value: orderValue
        ? this.formatCurrency(orderValue, backend.currency || 'USD')
        : this.i18n.t('common.tbd'),
      valuePending: !orderValue,
      statusId: statusId as OrderStatusId,
      status: backend.status,
      closed: backend.closed === true,
      statusLabel:
        backend.current_status_label || backend.status_display || statusConfig?.label || 'Unknown',
      pendingAction: statusConfig
        ? {
            icon: statusConfig.icon,
            text: statusConfig.description || statusConfig.label,
            urgent: statusConfig.color === 'urgent',
            actionLabel: statusConfig.label,
          }
        : null,
      nextAction: backend.pending_action?.action ?? null,
      items: backend.items || [],
      timeline: (() => {
        if (backend.timeline && backend.timeline.length > 0) {
          return backend.timeline.map((t: any) => ({
            status: t.status,
            label: t.label,
            shortLabel: t.short_label,
            icon: t.icon,
            blockedReason: t.blocked_reason,
          }));
        }
        // Build timeline from current phase using PIPELINE_STAGES
        const currentPhase = backend.current_phase || phase?.phase || 1;
        return PIPELINE_STAGES.map((stage) => {
          let status: 'done' | 'current' | 'pending';
          if (stage.phase < currentPhase) {
            status = 'done';
          } else if (stage.phase === currentPhase) {
            status = 'current';
          } else {
            status = 'pending';
          }
          return {
            status,
            label: stage.label,
            shortLabel: stage.shortLabel,
            icon: status === 'done' ? 'check' : status,
            blockedReason: undefined,
          };
        });
      })(),
      eta: backend.estimated_delivery_date || this.i18n.t('common.tbd'),
      etaTbd: !backend.estimated_delivery_date,
      createdAt: backend.created_at || new Date().toISOString(),
      updatedAt: backend.updated_at || new Date().toISOString(),
      isDelayed: backend.is_delayed || false,
      priority: (backend.priority as 'P1' | 'P2' | 'P3') || 'P2',
      progressPercent: backend.progress ?? (phase ? this.calculateProgress(phase.phase) : 0),
      supplier: backend.supplier || { id: 'TBD', name: 'TBD', color: '#9ca3af' },
      assignee: backend.assignee
        ? {
            id: backend.assignee.id || 'unknown',
            initials: backend.assignee.initials || 'UN',
            name: backend.assignee.name || this.i18n.t('common.unassigned'),
            gradientFrom: backend.assignee.gradient_from || '#9ca3af',
            gradientTo: backend.assignee.gradient_to || '#6b7280',
          }
        : {
            id: 'unassigned',
            initials: 'UN',
            name: this.i18n.t('common.unassigned'),
            gradientFrom: '#9ca3af',
            gradientTo: '#6b7280',
          },
      documents: backend.documents || [],
      missingItems: backend.missing_items || [],
      daysInCurrentStage: backend.days_in_current_stage || 0,
      lastActivity: this.formatLastActivity(backend.updated_at),
      workflowState: {
        orderId: backend.order_number || backend.id?.toString() || 'unknown',
        currentStatusId: statusId as OrderStatusId,
        currentPhase: backend.current_phase || phase?.phase || 1,
        previousStatusId: backend.previous_status_id as OrderStatusId,
        completedStatusIds: (backend.completed_statuses || []) as OrderStatusId[],
        parallelStatuses: backend.parallel_workflows || {},
        history: [],
        createdAt: backend.created_at || new Date().toISOString(),
        updatedAt: backend.updated_at || new Date().toISOString(),
        isBlocked: backend.is_blocked || false,
        blockReason: backend.block_reason,
      },
      // Payment and shipping fields from backend
      order_type: backend.order_type,
      days_to_payment: backend.days_to_payment,
      bl_date: backend.bl_date,
      // Financial data from backend API
      total: backend.total,
      payment_records: backend.payment_records,
      financials: backend.financials,
    } as ApiOrder;
  }

  /**
   * Map backend status transition to frontend format
   */
  private mapTransition = (backend: any): StatusTransition => ({
    id: backend.transition_id,
    from: backend.from_status_id,
    to: backend.to_status_id,
    timestamp: backend.timestamp,
    triggeredBy: backend.triggered_by,
    userId: backend.user_id,
    userName: backend.user_name,
    notes: backend.notes,
    metadata: backend.metadata,
  });

  /**
   * Build minimal order from workflow summary (after status transition)
   * Note: This builds a partial order with workflow data only.
   * Components should refetch full order data if they need all fields.
   */
  private buildOrderFromWorkflow(orderId: string, workflow: any): any {
    const statusId = workflow.currentStatus?.id || 'REVIEW';
    const phase = workflow.currentStatus?.phase || 1;

    return {
      id: orderId,
      statusId: statusId as OrderStatusId,
      statusLabel: workflow.currentStatus?.label || 'Unknown',
      workflowState: {
        orderId,
        currentStatusId: statusId as OrderStatusId,
        currentPhase: phase,
        previousStatusId: workflow.previousStatus?.id as OrderStatusId,
        completedStatusIds: (workflow.completedStatuses || []).map(
          (s: any) => s.id
        ) as OrderStatusId[],
        parallelStatuses: workflow.parallelWorkflows || {},
        history: [],
        createdAt: workflow.createdAt || new Date().toISOString(),
        updatedAt: workflow.updatedAt || new Date().toISOString(),
        isBlocked: workflow.isBlocked || false,
        blockReason: workflow.blockReason,
      },
      timeline: workflow.timeline?.stages || [],
      updatedAt: workflow.updatedAt || new Date().toISOString(),
    };
  }

  /**
   * Calculate progress percentage from phase
   */
  private calculateProgress(phase: number): number {
    const phaseProgress: Record<number, number> = {
      1: 5,
      2: 10,
      3: 20,
      4: 40,
      5: 60,
      6: 75,
      7: 85,
      8: 90,
      9: 95,
    };
    return phaseProgress[phase] || 0;
  }

  /**
   * Format last activity timestamp
   */
  private formatLastActivity(timestamp: string): string {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }

  /**
   * Format currency value with proper formatting
   * Converts "51625.6000" to "$51,625.60"
   */
  private formatCurrency(value: string | number, currency = 'USD'): string {
    return formatMoney(value, currency) || 'TBD';
  }

  // ==========================================================================
  // ORDER WORKFLOW
  // ==========================================================================

  /**
   * Get workflow state for an order.
   * Endpoint: GET /api/orders/workflow/orders/{order_id}/workflow/
   * Stale-while-revalidate cached.
   */
  getOrderWorkflow(orderId: string | number): Observable<any> {
    const key = orderId.toString();
    const cached = this.workflowCache.get(key);
    const stale = !cached || Date.now() - cached.fetchedAt > WorkflowOrderService.DETAIL_TTL_MS;

    if (cached && !stale) return of(cached.data);
    const fresh$ = this.joinedRead(
      this.workflowInFlight,
      this.workflowCache,
      key,
      `${this.apiUrl}/${key}/workflow/`
    );
    if (cached && stale) {
      fresh$.subscribe({
        error: () => {
          /* keep using cached */
        },
      });
      return of(cached.data);
    }
    return fresh$;
  }

  /**
   * Transition workflow status using the new simplified workflow
   * Endpoint: PATCH /api/orders/workflow/orders/{order_id}/status/
   *
   * @param orderId - The order ID
   * @param request - Status transition request with toStatusId, optional notes and metadata
   */
  transitionWorkflowStatus(
    orderId: string | number,
    request: {
      toStatusId: OrderStatusId;
      notes?: string;
      metadata?: Record<string, any>;
    }
  ): Observable<{
    success: boolean;
    message?: string;
    currentStatus: { id: OrderStatusId; label: string; phase?: number };
    transitionId?: number;
  }> {
    const body = {
      to_status_id: request.toStatusId,
      notes: request.notes,
      metadata: request.metadata,
    };

    return this.http.patch<any>(`${this.apiUrl}/${orderId}/status/`, body).pipe(
      map((response) => ({
        success: response.success ?? true,
        message: response.message || 'Status updated successfully',
        currentStatus: {
          id: response.currentStatus?.id || request.toStatusId,
          label:
            response.currentStatus?.label ||
            getStatusConfig(request.toStatusId)?.label ||
            request.toStatusId,
          phase: response.currentStatus?.phase,
        },
        transitionId: response.transitionId,
      }))
    );
  }

  /**
   * Block workflow for an order
   * Endpoint: POST /api/orders/workflow/orders/{order_id}/block/
   */
  blockWorkflow(
    orderId: string | number,
    reason: string
  ): Observable<{ success: boolean; message: string; isBlocked: boolean; blockReason: string }> {
    return this.http.post<any>(`${this.apiUrl}/${orderId}/block/`, { reason }).pipe(
      map((response) => ({
        success: response.success ?? true,
        message: response.message || 'Workflow blocked successfully',
        isBlocked: response.isBlocked ?? true,
        blockReason: response.blockReason || reason,
      }))
    );
  }

  /**
   * Record that the approved proforma went to the customer another way (no email).
   * Endpoint: POST /api/orders/workflow/orders/{order_id}/proforma-skip/
   * Answers the workflow summary with proformaSkipped { at, by }; the status stays.
   */
  skipProforma(
    orderId: string | number
  ): Observable<{ proformaSkipped: { at: string; by: string } | null }> {
    return this.http
      .post<{ proformaSkipped: { at: string; by: string } | null }>(
        `${this.apiUrl}/${orderId}/proforma-skip/`,
        {}
      )
      .pipe(
        // The answer is the fresh workflow summary: keep it, so an older GET cannot win
        tap((workflow) =>
          this.workflowCache.set(orderId.toString(), { data: workflow, fetchedAt: Date.now() })
        )
      );
  }

  /**
   * Unblock workflow for an order
   * Endpoint: POST /api/orders/workflow/orders/{order_id}/unblock/
   */
  unblockWorkflow(
    orderId: string | number
  ): Observable<{ success: boolean; message: string; isBlocked: boolean }> {
    return this.http.post<any>(`${this.apiUrl}/${orderId}/unblock/`, {}).pipe(
      map((response) => ({
        success: response.success ?? true,
        message: response.message || 'Workflow unblocked successfully',
        isBlocked: response.isBlocked ?? false,
      }))
    );
  }

  // ==========================================================================
  // SUPPLIER RECOMMENDATIONS (for Order Timeline)
  // ==========================================================================

  /**
   * Get AI supplier recommendations for an order
   * Endpoint: GET /api/orders/orders/{order_id}/supplier_recommendations/
   */
  getSupplierRecommendations(orderId: string | number): Observable<any> {
    return this.http.get<any>(
      `${environment.apiBaseUrl}/orders/orders/${orderId}/supplier_recommendations/`
    );
  }

  /**
   * Generate new supplier recommendations using AI matching
   * Endpoint: POST /api/procurement/suppliers/llm_match/
   */
  matchSuppliers(orderId: string | number, request?: any): Observable<any> {
    const body = {
      order_id: orderId,
      limit: request?.limit || 5,
      include_llm_analysis: request?.include_llm_analysis !== false,
      recalculate: request?.recalculate === true, // Only true if explicitly set
    };
    return this.http.post<any>(`${environment.apiBaseUrl}/procurement/suppliers/llm_match/`, body);
  }
}
